// src/components/LinksManager.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import { saveUserLinks } from "./firebaseOperations";
import { Crown } from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import styles from "./LinksManager.module.css";
import { useProFeature } from "../context/ProFeatureContext";
import { generatePassword, hashPassword } from "./LinksManagerPasswordUtils";
import { uploadImageToStorage } from "./LinksManagerImageUtils";
import LinksManagerSortableLink from "./LinksManagerSortableLink";
import LinksManagerAddForm from "./LinksManagerAddForm";

// Set link limits by tier
const FREE_LINK_LIMIT = 5;
const PRO_LINK_LIMIT = 25;

// Format URL function
const formatUrl = (url) => {
  // Trim any whitespace
  let formattedUrl = url.trim();
  
  // If URL is empty, return as is
  if (!formattedUrl) return formattedUrl;

  // Check if the URL already has a protocol (http://, https://, ftp://, etc.)
  const hasProtocol = /^[a-zA-Z]+:\/\//.test(formattedUrl);
  
  // If no protocol is present, add https://
  if (!hasProtocol) {
    formattedUrl = `https://${formattedUrl}`;
  }
  
  return formattedUrl;
};

const LinksManager = ({
  userId,
  initialLinks = [],
  onSave,
  isProUser,
  onUpgradeClick,
}) => {
  // Access the ProFeatureContext
  const { showProFeatureNotification } = useProFeature();

  const [links, setLinks] = useState(
    initialLinks.map((link, index) => ({
      ...link,
      hidden: link.hidden || false,
      ghostProtected: link.ghostProtected || false,
      pinned: link.pinned || false,
      order: link.order || index,
      imageUrl: link.imageUrl || null,
    }))
  );
  const [newLink, setNewLink] = useState({ title: "", url: "" });
  const [editingId, setEditingId] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [freeBadgeLinkId, setFreeBadgeLinkId] = useState(null);
  const [uploadingLinkId, setUploadingLinkId] = useState(null);
  const [statusLabel, setStatusLabel] = useState(null);
  const [animatingLinkId, setAnimatingLinkId] = useState(null);

  const formInputRef = useRef(null);
  const [activeUploadLinkId, setActiveUploadLinkId] = useState(null);
  const fileInputRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 10 } })
  );

  // Function to show temporary status label
  const showStatusLabel = (linkId, text) => {
    setStatusLabel({ linkId, text });
    setTimeout(() => {
      setStatusLabel(null);
    }, 3000); // Show for 3 seconds
  };

  // Handle copy password
  const handleCopyPassword = (password) => {
    navigator.clipboard.writeText(password);
    showStatusLabel(null, "Password copied to clipboard!");
  };

  // Add status text to show when image is being processed
  const updateImageSizeStatus = (file) => {
    if (!file) return;
    
    const img = new Image();
    img.onload = () => {
      const width = img.width;
      const height = img.height;
      const fileSizeMB = file.size / (1024 * 1024);
      
      if (width > 800 || height > 800 || fileSizeMB > 2) {
        showStatusLabel(
          activeUploadLinkId,
          `Image will be resized (${width}×${height}px, ${fileSizeMB.toFixed(1)}MB)`
        );
      }
    };
    
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  // Handler for image upload
  const handleImageUpload = (linkId) => {
    setActiveUploadLinkId(linkId);
    
    // Create hidden file input if it doesn't exist
    if (!fileInputRef.current) {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.style.display = "none";
      document.body.appendChild(input);
      fileInputRef.current = input;
    }
    
    // Set up event handler
    fileInputRef.current.onchange = (e) => handleFileSelected(e);
    fileInputRef.current.click();
  };

  // Handler for file selection
  const handleFileSelected = async (e) => {
    const file = e.target.files[0];
    if (!file || !isProUser || !activeUploadLinkId) {
      console.log("File selection cancelled or error:", {
        file: !!file,
        isProUser,
        activeUploadLinkId,
      });
      return;
    }

    // Check if file is an image
    if (!file.type.match('image.*')) {
      alert("Please select an image file (JPEG, PNG, GIF, etc.)");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setUploadingLinkId(activeUploadLinkId);
    showStatusLabel(activeUploadLinkId, "Processing image...");

    try {
      // Get file size in MB
      const fileSizeMB = file.size / (1024 * 1024);
      console.log(`Selected image: ${file.name}, Size: ${fileSizeMB.toFixed(2)} MB`);

      // Show detailed information about image size
      updateImageSizeStatus(file);

      // Short delay to allow the status message to be read
      if (fileSizeMB > 2) {
        await new Promise(resolve => setTimeout(resolve, 2000));
      }

      console.log("Uploading image for link:", activeUploadLinkId);
      // Upload the image (it will be resized if needed)
      const imageUrl = await uploadImageToStorage(
        userId,
        activeUploadLinkId,
        file
      );

      console.log("Image upload successful, URL:", imageUrl);

      // Update the link with the image URL
      setLinks((prev) => {
        const newLinks = prev.map((link) =>
          link.id === activeUploadLinkId ? { ...link, imageUrl } : link
        );
        console.log(
          "Updated link with image URL:",
          newLinks.find((l) => l.id === activeUploadLinkId)
        );
        return newLinks;
      });

      showStatusLabel(activeUploadLinkId, "Image uploaded successfully!");
      setHasChanges(true);
    } catch (error) {
      console.error("Error uploading image:", error);
      showStatusLabel(activeUploadLinkId, "Error uploading image");
      alert("Failed to upload image. Please try again. " + error.message);
    } finally {
      setUploadingLinkId(null);
      setActiveUploadLinkId(null);
      // Reset the file input value to allow selecting the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Handler to remove image
  const handleImageRemove = (linkId) => {
    setLinks(
      links.map((link) =>
        link.id === linkId ? { ...link, imageUrl: null } : link
      )
    );
    showStatusLabel(linkId, "Image removed");
    setHasChanges(true);
  };

  const addLink = () => {
    // Check if user has reached their limit
    if (links.length >= (isProUser ? PRO_LINK_LIMIT : FREE_LINK_LIMIT)) {
      if (!isProUser) {
        // Only show notification, don't automatically redirect
        showProFeatureNotification(
          `Upgrade to Pro to add up to ${PRO_LINK_LIMIT} links`
        );
      }
      return;
    }

    if (newLink.title && newLink.url) {
      // Format the URL before adding
      const formattedUrl = formatUrl(newLink.url);
      
      setLinks([
        ...links,
        {
          ...newLink,
          url: formattedUrl, // Use the formatted URL
          id: Date.now(),
          hidden: false,
          ghostProtected: false,
          pinned: false,
          order: links.length,
          imageUrl: null,
        },
      ]);
      setNewLink({ title: "", url: "" });
      setHasChanges(true);

      // Focus back on the title input after adding
      if (formInputRef.current) {
        formInputRef.current.focus();
      }
    }
  };

  const removeLink = (id) => {
    setLinks(links.filter((link) => link.id !== id));
    showStatusLabel(null, "Link removed");
    setHasChanges(true);
  };

  const toggleLinkVisibility = (id) => {
    if (!isProUser) {
      // For free users, show temporary badge and notification without auto-redirecting
      handleFreeFeatureAttempt(id);
      return;
    }
    setLinks(
      links.map((link) => {
        if (link.id === id) {
          const newHidden = !link.hidden;
          showStatusLabel(id, newHidden ? "Link hidden" : "Link visible");
          return {
            ...link,
            hidden: newHidden,
            // Disable ghost protection when hiding
            ghostProtected: newHidden ? false : link.ghostProtected,
            ghostPassword: newHidden ? null : link.ghostPassword,
            ghostPasswordHash: newHidden ? null : link.ghostPasswordHash,
          };
        }
        return link;
      })
    );
    setHasChanges(true);
  };

  // Toggle ghost protection
  const toggleGhostProtection = async (id) => {
    if (!isProUser) {
      // Show notification without auto-redirecting
      handleFreeFeatureAttempt(id);
      return;
    }
    setLinks((prevLinks) => {
      const newLinks = prevLinks.map((link) => {
        if (link.id === id) {
          if (link.hidden) return link;
          if (!link.ghostProtected) {
            const password = generatePassword();
            hashPassword(password).then((hash) => {
              setLinks((currentLinks) =>
                currentLinks.map((l) =>
                  l.id === id ? { ...l, ghostPasswordHash: hash } : l
                )
              );
            });
            showStatusLabel(id, "Ghost protection enabled");
            return {
              ...link,
              ghostProtected: true,
              ghostPassword: password,
            };
          } else {
            showStatusLabel(id, "Ghost protection disabled");
            return {
              ...link,
              ghostProtected: false,
              ghostPassword: null,
              ghostPasswordHash: null,
            };
          }
        }
        return link;
      });
      return newLinks;
    });
    setHasChanges(true);
  };

  // Toggle pin status of a link with glass-like animation
  const togglePinStatus = (id) => {
    if (!isProUser) {
      // Show notification without auto-redirecting
      handleFreeFeatureAttempt(id);
      return;
    }
    
    // Prevent multiple animations at once
    if (animatingLinkId) return;
    
    // Start glass animation
    setAnimatingLinkId(id);
    
    // Show status but don't move immediately
    const targetLink = links.find(link => link.id === id);
    const willBePinned = !targetLink.pinned;
    showStatusLabel(id, willBePinned ? "Pinning..." : "Unpinning...");
    
    // Animate with a slight delay before changing the actual order
    setTimeout(() => {
      setLinks(
        links.map((link) => {
          if (link.id === id) {
            return {
              ...link,
              pinned: willBePinned,
            };
          }
          return link;
        })
      );
      setHasChanges(true);
      
      // Update status message
      showStatusLabel(id, willBePinned ? "Link pinned" : "Link unpinned");
      
      // Keep the animation going a bit longer for smooth effect
      setTimeout(() => {
        setAnimatingLinkId(null);
      }, 700); // Match the CSS animation duration
    }, 400); // Delay the actual position change
  };

  const startEditing = (link) => {
    setEditingId(link.id);
    setNewLink({ title: link.title, url: link.url });
  };

  const updateLink = () => {
    if (newLink.title && newLink.url) {
      // Format the URL before updating
      const formattedUrl = formatUrl(newLink.url);
      
      setLinks(
        links.map((link) =>
          link.id === editingId
            ? { ...link, title: newLink.title, url: formattedUrl }
            : link
        )
      );
      setNewLink({ title: "", url: "" });
      setEditingId(null);
      showStatusLabel(editingId, "Link updated");
      setHasChanges(true);
    }
  };

  // Sort links with pinned items first, then by order
  const sortedLinks = [...links].sort((a, b) => {
    if (a.pinned && !b.pinned) return -1;
    if (!a.pinned && b.pinned) return 1;
    return a.order - b.order;
  });

  // For pro users, update the order when drag ends.
  const handleDragEndPro = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = sortedLinks.findIndex((link) => link.id === active.id);
      const newIndex = sortedLinks.findIndex((link) => link.id === over.id);
      const newLinks = arrayMove(sortedLinks, oldIndex, newIndex);

      // Update order values to match new positions
      const updatedLinks = newLinks.map((link, index) => ({
        ...link,
        order: index,
      }));

      setLinks(updatedLinks);
      showStatusLabel(active.id, "Order updated");
      setHasChanges(true);
    }
  };

  // For free users, allow drag movement but do not update the order.
const handleDragEndFree = (event) => {
  const { active } = event;
  // Permanently show the "Pro Feature" badge on the dragged link.
  setFreeBadgeLinkId(active.id);

  // Show pro feature notification without auto-redirect
  showProFeatureNotification("Upgrade to Pro to reorder your links");
};

// For click attempts on pro-only features (visibility, ghost), show badge temporarily.
const handleFreeFeatureAttempt = (linkId) => {
  setFreeBadgeLinkId(linkId);
  setTimeout(() => {
    setFreeBadgeLinkId(null);
  }, 2000);

  // Show the Pro Feature notification without auto-redirect
  showProFeatureNotification(
    "Upgrade to Pro to use all link customization features"
  );
};

const handleSave = useCallback(async () => {
  try {
    // Log the links being saved
    console.log(
      "Saving links with images:",
      links.map((link) => ({
        id: link.id,
        title: link.title,
        hasImage: !!link.imageUrl,
      }))
    );

    await saveUserLinks(userId, links);
    if (onSave) onSave(links);
    setHasChanges(false);
  } catch (error) {
    console.error("Error saving links:", error);
  }
}, [links, userId, onSave]);

useEffect(() => {
  if (hasChanges) {
    const timer = setTimeout(() => {
      handleSave();
    }, 1000);
    return () => clearTimeout(timer);
  }
}, [hasChanges, handleSave]);

return (
  <div className={styles.linksManager}>
    <div className={styles.linkLimitInfo}>
      <div className={styles.limitCounter}>
        {!isProUser && (
          <div className={styles.upgradeTip}>
            <Crown size={14} className={styles.crownIcon} />
            <span className={styles.upgradetopro}>
              Upgrade to Pro for {PRO_LINK_LIMIT} links
            </span>
          </div>
        )}
      </div>
    </div>

    <LinksManagerAddForm
      newLink={newLink}
      setNewLink={setNewLink}
      addLink={addLink}
      updateLink={updateLink}
      editingId={editingId}
      links={links}
      isProUser={isProUser}
      formInputRef={formInputRef}
    />

    {isProUser ? (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEndPro}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext
          items={sortedLinks.map((link) => link.id)}
          strategy={verticalListSortingStrategy}
        >
          {sortedLinks.map((link) => (
            <LinksManagerSortableLink
              key={link.id}
              link={link}
              isProUser={true}
              onUpgradeClick={onUpgradeClick}
              onToggleVisibility={toggleLinkVisibility}
              onToggleGhost={toggleGhostProtection}
              onTogglePin={togglePinStatus}
              onStartEditing={startEditing}
              onRemove={removeLink}
              onImageUpload={handleImageUpload}
              onImageRemove={handleImageRemove}
              handleFreeFeatureAttempt={handleFreeFeatureAttempt}
              isUploading={uploadingLinkId === link.id}
              onCopyPassword={handleCopyPassword}
              statusLabel={statusLabel}
              animatingLinkId={animatingLinkId}
            />
          ))}
        </SortableContext>
      </DndContext>
    ) : (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEndFree}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext
          items={links.map((link) => link.id)}
          strategy={verticalListSortingStrategy}
        >
          {links.map((link) => (
            <React.Fragment key={link.id}>
              <LinksManagerSortableLink
                link={link}
                isProUser={false}
                onUpgradeClick={onUpgradeClick}
                onToggleVisibility={() => handleFreeFeatureAttempt(link.id)}
                onToggleGhost={() => handleFreeFeatureAttempt(link.id)}
                onTogglePin={() => handleFreeFeatureAttempt(link.id)}
                onImageUpload={() => handleFreeFeatureAttempt(link.id)}
                onImageRemove={() => {}}
                onStartEditing={startEditing}
                onRemove={removeLink}
                handleFreeFeatureAttempt={handleFreeFeatureAttempt}
                isUploading={false}
                onCopyPassword={() => {}}
                statusLabel={statusLabel}
                animatingLinkId={animatingLinkId}
              />
              {freeBadgeLinkId === link.id && (
                <div className={styles.proFeatureLabel}>
                  <Crown size={14} className={styles.menuCrown} />
                  <span>Pro Feature</span>
                </div>
              )}
            </React.Fragment>
          ))}
        </SortableContext>
      </DndContext>
    )}
  </div>
);
};

export default LinksManager;